import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useHistory, useRouteMatch } from 'react-router-dom';

import StaticAdsService from 'services/StaticAdsService';
import { shouldPoll } from 'shared/pollingGate';

import { onStaticActivityBump } from './activityBus';

const POLL_MS = 5000;
const DONE_FLASH_MS = 6000;

/**
 * The global "what's happening" strip, shown beside the tabs on every Static
 * Creative tab: `N analyzing`, `N generating`, and a brief ✓ flash when a kind
 * finishes. One narrow count endpoint; polls ONLY while something is in flight
 * (plus one probe on page load and whenever a queue action bumps the bus), so
 * an idle dashboard keeps Snowflake asleep. Chips navigate to where the work
 * lands: analyzing → Library, generating → Output.
 */
export default function ActivityStrip() {
  const service = useMemo(() => new StaticAdsService(), []);
  const history = useHistory();
  const { url } = useRouteMatch();

  const [counts, setCounts] = useState<{ analyzing: number; generating: number }>({ analyzing: 0, generating: 0 });
  const [doneFlash, setDoneFlash] = useState<string | null>(null);

  const prev = useRef(counts);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const stop = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };

    const fetchOnce = async () => {
      if (!shouldPoll()) return;
      const resp = await service.getActivity().catch(() => null);
      if (cancelled || !resp) return;
      const finished: string[] = [];
      if (prev.current.analyzing > 0 && resp.analyzing === 0) finished.push('Analysis');
      if (prev.current.generating > 0 && resp.generating === 0) finished.push('Generation');
      prev.current = resp;
      setCounts(resp);
      if (finished.length) {
        setDoneFlash(`${finished.join(' & ')} finished`);
        if (flashTimer.current) clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setDoneFlash(null), DONE_FLASH_MS);
      }
      if (resp.analyzing + resp.generating === 0) stop();
      else if (!timer.current) timer.current = setInterval(fetchOnce, POLL_MS);
    };

    // One probe on load catches work already in flight; after that, polling
    // runs only while the backend reports something active.
    void fetchOnce();
    const unsubscribe = onStaticActivityBump(() => {
      // The queue insert is in flight when the bump fires — give it a beat.
      setTimeout(() => { void fetchOnce(); }, 800);
      if (!timer.current) timer.current = setInterval(fetchOnce, POLL_MS);
    });

    return () => {
      cancelled = true;
      stop();
      if (flashTimer.current) clearTimeout(flashTimer.current);
      unsubscribe();
    };
  }, [service]);

  const spinner = <CircularProgress size={12} color="inherit" />;

  if (!counts.analyzing && !counts.generating && !doneFlash) return null;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {counts.analyzing > 0 && (
        <Chip
          size="small" color="info" icon={spinner} clickable
          label={`${counts.analyzing} analyzing`}
          onClick={() => history.push(`${url}/library`)}
        />
      )}
      {counts.generating > 0 && (
        <Chip
          size="small" color="secondary" icon={spinner} clickable
          label={`${counts.generating} generating`}
          onClick={() => history.push(`${url}/output`)}
        />
      )}
      {doneFlash && (
        <Chip size="small" color="success" icon={<CheckCircleOutlineIcon />} label={doneFlash} />
      )}
    </Stack>
  );
}
